import { For, Show } from "solid-js";
import type { OnlinePlayer } from "../services/onlinePlayersApi";
import Text from "./Text";
import { formatDateTime } from "../services/dateFormat";
import styles from "./OnlinePlayersList.module.css";

function relativeTime(iso: string, now: number): string {
  const then = new Date(iso).getTime();
  if (!isFinite(then) || then === 0) return "-";
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function elapsedSince(iso: string | null, now: number): string {
  if (!iso) return "-";
  return relativeTime(iso, now);
}

function absoluteTime(iso: string): string {
  return formatDateTime(iso, "");
}

interface Props {
  players: OnlinePlayer[];
  now: number;
  onShowHistory?: (playerName: string) => void;
}

export default function OnlinePlayersList(props: Props) {
  const onlineCount = () => props.players.filter(p => p.currentlyOnline).length;

  return (
    <div class={styles.wrapper}>
      <Show when={props.players.length > 0} fallback={
        <div class={styles.empty}>
          <i class="fa-solid fa-user-slash" />
          <p><Text val="ONLINE_PLAYERS_EMPTY" /></p>
        </div>
      }>
        <div class={styles.summary}>
          <span class={styles.onlineCount}>{onlineCount()}</span>
          <span class={styles.onlineLabel}><Text val="ONLINE_PLAYERS_ONLINE_NOW" /></span>
          <span class={styles.totalLabel}>
            <Text val="ONLINE_PLAYERS_TOTAL_KNOWN" /> {props.players.length}
          </span>
        </div>
        <div class={styles.list}>
          <For each={props.players}>
            {(player) => (
              <div class={styles.row} classList={{ [styles.offline]: !player.currentlyOnline }}>
                <span class={styles.pill} classList={{
                  [styles.pillOnline]: player.currentlyOnline,
                  [styles.pillOffline]: !player.currentlyOnline,
                }}>
                  {player.currentlyOnline ? "ON" : "OFF"}
                </span>
                <span class={styles.name}>{player.name}</span>
                <span class={styles.timestamps}>
                  <Show when={player.currentlyOnline} fallback={
                    <span class={styles.tsBlock} title={absoluteTime(player.firstSeen)}>
                      <span class={styles.tsLabel}><Text val="ONLINE_PLAYERS_FIRST_SEEN" /></span>
                      <span class={styles.tsValue}>{relativeTime(player.firstSeen, props.now)}</span>
                    </span>
                  }>
                    <span class={styles.tsBlock} title={player.currentSessionStart ? absoluteTime(player.currentSessionStart) : ""}>
                      <span class={styles.tsLabel}><Text val="ONLINE_PLAYERS_ONLINE_FOR" /></span>
                      <span class={styles.tsValue}>{elapsedSince(player.currentSessionStart, props.now)}</span>
                    </span>
                  </Show>
                  <span class={styles.tsBlock} title={absoluteTime(player.lastSeen)}>
                    <span class={styles.tsLabel}><Text val="ONLINE_PLAYERS_LAST_SEEN" /></span>
                    <span class={styles.tsValue}>{relativeTime(player.lastSeen, props.now)}</span>
                  </span>
                  <Show when={props.onShowHistory}>
                    <button
                      type="button"
                      class="btn btn-secondary btn-sm btn-icon"
                      title="View online history"
                      onClick={() => props.onShowHistory!(player.name)}
                    >
                      <i class="fa-solid fa-clock-rotate-left" />
                    </button>
                  </Show>
                </span>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
